import { useState }    from "react";
import { Check, CheckCircle } from "lucide-react";

import SignNowStep             from "../SignNowStep";
import { ONBOARDING_DOCS }    from "../../lib/signnow";

// Spin keyframe needed by SignNowStep's Loader2
const SPIN_STYLE = <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>;

/**
 * DocumentsTab
 * Lists all onboarding documents and renders a SignNowStep for each.
 * Tracks local signed state so the progress bar updates immediately.
 *
 * Props:
 *   user        – current user object
 *   onDocSigned – callback(docKey) propagates signed state up to the page
 */
export default function DocumentsTab({ user, onDocSigned }) {
  const [signed, setSigned] = useState(
    Object.fromEntries(
      ONBOARDING_DOCS.map(d => [d.docKey, user?.onboarding?.[d.onboardingKey] ?? false])
    )
  );

  function handleSigned(docKey) {
    setSigned(s => ({ ...s, [docKey]: true }));
    onDocSigned?.(docKey);
  }

  const signedCount = Object.values(signed).filter(Boolean).length;
  const total       = ONBOARDING_DOCS.length;
  const allSigned   = signedCount === total;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {SPIN_STYLE}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Onboarding Documents</span>
          {allSigned
            ? <span className="badge badge-green"><Check size={12} /> All signed</span>
            : <span className="badge badge-proposal">{signedCount}/{total} signed</span>
          }
        </div>

        <div className="card-body">
          {!allSigned && (
            <>
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 8 }}>
                All {total} documents are required before your account is fully activated.
                Click <strong>Sign Now</strong> on any pending document — the signing window opens right here.
              </p>
              <div style={{ background: "#e2e8f0", borderRadius: 6, height: 6, marginBottom: 20, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.round((signedCount / total) * 100)}%`,
                  height: "100%", background: "#2563eb", borderRadius: 6, transition: "width 0.4s",
                }} />
              </div>
            </>
          )}

          {allSigned && (
            <div className="agent-banner" style={{ marginBottom: 20 }}>
              <CheckCircle size={18} color="#16a34a" style={{ flexShrink: 0 }} />
              <div className="agent-banner-text">
                <strong>All documents signed</strong>
                <span>Your account is fully activated. Training and goal-setting are now unlocked.</span>
              </div>
            </div>
          )}

          {ONBOARDING_DOCS.map(({ docKey, docLabel, templateId }) => (
            <SignNowStep
              key={docKey}
              docKey={docKey}
              docLabel={docLabel}
              templateId={templateId}
              signerEmail={user?.email ?? ""}
              signerName={user?.full_name ?? ""}
              onSigned={handleSigned}
              signed={signed[docKey]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}